import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Loader2 } from 'lucide-react';
import { api, getApiErrorMessage } from '../../api/client';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { usePositions } from '../../hooks/usePositions';
import { queryLoadFailure, queryFailureError } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import ConfirmModal from '../../components/ui/ConfirmModal';
import {
  LINE_ITEM_TYPE_LABELS,
  LINE_ITEM_TYPE_OPTIONS,
  type LineItemType,
} from '../../constants/line-item-types';
import type { Position } from '../../types/position';

/**
 * The tenant's positions (`positions`) — what a hand is (Tong Technician,
 * Crew Chief…), and which line item types a hand in that position works.
 *
 * Until this page the catalog was reachable only inline on the employee
 * profile's position select. `/settings/team/positions` redirects to the
 * Trucking tab, which manages `trucking_positions` — a different table.
 *
 * Modelled on `EquipmentTypesPage`. What differs:
 *
 *   - A STANDARD POSITION IS HALF-LOCKED. The API refuses rename, deactivate
 *     and delete (403), but takes `line_item_types` on any position (0425).
 *     So a standard row keeps its line-type control and loses the other three.
 *   - DELETE NEVER ANSWERS "IN USE". An in-use position (anyone's primary OR
 *     in anyone's "can work as" set) is deactivated instead, and the envelope
 *     says so in `method`. A failure is a real failure, and its message is the
 *     API's, shown as sent.
 *
 * Every write invalidates BOTH `['positions']` (the pickers, the Team filter)
 * and the user queries (`['users']`, `['user']`), because a user payload
 * carries `positionName` and a deactivate changes what a profile can offer.
 *
 * Cross-vertical: no `hasVertical` guard, here or on the nav entry.
 */

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';
const pillCls = 'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-fieldiq-border text-fieldiq-muted';

export default function PositionsPage() {
  const queryClient = useQueryClient();

  /*
   * POST, PATCH and DELETE /positions are non-strict requireRole('admin'):
   * admin, or a dispatcher with dispatcher_admin_access. Not owner. GET is
   * ungated, so the list shows to everyone and only the controls hide.
   */
  const isAdmin = useCanWriteCompanySettings();

  const [addName, setAddName] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  /** The position whose line-type checklist is open, and its draft set. */
  const [typesId, setTypesId] = useState<string | null>(null);
  const [typesDraft, setTypesDraft] = useState<Set<LineItemType>>(new Set());
  const [saving, setSaving] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const [rowNotice, setRowNotice] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Position | null>(null);

  // Asks for deactivated rows: a page that cannot see what it deactivated
  // has made Deactivate a one-way door.
  const query = usePositions({ includeInactive: true });
  const positions = query.data ?? [];
  const failure = queryLoadFailure(query);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['positions'] });
    queryClient.invalidateQueries({ queryKey: ['users'] });
    queryClient.invalidateQueries({ queryKey: ['user'] });
  };

  const clearRowMessages = () => { setRowError(null); setRowNotice(null); };

  const handleAdd = async () => {
    const name = addName.trim();
    if (!name) return;
    setSaving(true);
    setAddError(null);
    try {
      await api.positions.create({ name });
      setAddName('');
      refresh();
    } catch (err) {
      setAddError(getApiErrorMessage(err, 'Failed to add position'));
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (id: string) => {
    const name = editName.trim();
    if (!name) return;
    setSaving(true);
    setEditError(null);
    try {
      await api.positions.update(id, { name });
      setEditId(null);
      refresh();
    } catch (err) {
      setEditError(getApiErrorMessage(err, 'Failed to rename position'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (p: Position) => {
    clearRowMessages();
    try {
      await api.positions.update(p.id, { isActive: !p.isActive });
      refresh();
    } catch (err) {
      setRowError(getApiErrorMessage(err, 'Failed to update position'));
    }
  };

  const openTypes = (p: Position) => {
    clearRowMessages();
    setTypesId(p.id);
    setTypesDraft(new Set(p.lineItemTypes));
  };

  const toggleDraftType = (t: LineItemType) => {
    setTypesDraft((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  };

  /** Sends the WHOLE set — PATCH replaces `line_item_types`, it does not merge. */
  const handleSaveTypes = async (id: string) => {
    setSaving(true);
    clearRowMessages();
    try {
      const lineItemTypes = LINE_ITEM_TYPE_OPTIONS.map((o) => o.value).filter((v) => typesDraft.has(v));
      await api.positions.update(id, { lineItemTypes });
      setTypesId(null);
      refresh();
    } catch (err) {
      setRowError(getApiErrorMessage(err, 'Failed to save line item types'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const p = confirmDelete;
    if (!p) return;
    setSaving(true);
    clearRowMessages();
    try {
      const res = await api.positions.remove(p.id);
      setRowNotice(
        res.data?.method === 'soft_delete'
          ? `“${p.name}” is still held by team members, so it was deactivated rather than deleted.`
          : `“${p.name}” was deleted.`,
      );
      refresh();
    } catch (err) {
      setRowError(getApiErrorMessage(err, 'Failed to delete position'));
    } finally {
      setSaving(false);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="min-h-full">
      {/* The People & access workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-3">
        <p className="text-xs text-fieldiq-muted">
          The positions a team member can hold, and which line item types each one works. A member has one
          primary position and can be set to work as others on their profile.
        </p>
        <p className="text-xs text-fieldiq-muted">
          Standard positions can't be renamed, deactivated or deleted, but you choose their line item types.
        </p>

        {!isAdmin && (
          <p className="text-xs text-fieldiq-muted">Only an admin can change positions.</p>
        )}

        {rowError && <p role="alert" className="text-xs text-red-400">{rowError}</p>}
        {rowNotice && <p className="text-xs text-fieldiq-muted">{rowNotice}</p>}

        {query.isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
          </div>
        )}

        {failure && <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="positions" />}

        {!query.isLoading && !failure && positions.length === 0 && (
          <div className="text-center py-12 text-fieldiq-muted text-sm">No positions yet</div>
        )}

        {!failure && positions.map((p) => (
          <div
            key={p.id}
            data-testid={`position-row-${p.id}`}
            className={`bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-2 ${p.isActive ? '' : 'opacity-60'}`}
          >
            {editId === p.id ? (
              <div>
                <div className="flex items-center gap-2">
                  <input
                    value={editName}
                    onChange={(e) => { setEditName(e.target.value); setEditError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRename(p.id); }}
                    className={`${inputCls} flex-1 ${editError ? 'border-red-500' : ''}`}
                    placeholder="Position name"
                    aria-label="Position name"
                    autoFocus
                  />
                  <button
                    onClick={() => handleRename(p.id)}
                    disabled={saving || !editName.trim()}
                    className="px-3 py-1.5 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button onClick={() => { setEditId(null); setEditError(null); }} className="px-3 py-1.5 text-xs text-fieldiq-text">
                    Cancel
                  </button>
                </div>
                {editError && <p className="text-xs text-red-400 mt-1.5">{editError}</p>}
              </div>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium text-white flex-1">{p.name}</span>
                {!p.isActive && <span className={pillCls}>Deactivated</span>}
                {p.isStandard && <span className={pillCls}>Standard</span>}
                {isAdmin && (
                  <>
                    <button
                      onClick={() => (typesId === p.id ? setTypesId(null) : openTypes(p))}
                      className="px-2 py-1 text-xs text-fieldiq-accent hover:text-white"
                      aria-expanded={typesId === p.id}
                      aria-label={`Line item types for ${p.name}`}
                    >
                      Line item types
                    </button>
                    {!p.isStandard && (
                      <>
                        <button
                          onClick={() => { setEditId(p.id); setEditName(p.name); setEditError(null); }}
                          className="px-2 py-1 text-xs text-fieldiq-accent hover:text-white"
                          aria-label={`Rename ${p.name}`}
                        >
                          Rename
                        </button>
                        <button
                          onClick={() => handleToggle(p)}
                          className="px-2 py-1 text-xs text-fieldiq-text hover:text-white"
                          aria-label={`${p.isActive ? 'Deactivate' : 'Activate'} ${p.name}`}
                        >
                          {p.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => { clearRowMessages(); setConfirmDelete(p); }}
                          className="px-2 py-1 text-xs text-red-400 hover:text-red-300"
                          aria-label={`Delete ${p.name}`}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* The current mapping, always visible. An empty set is a fact about the
                position — nobody has chosen any — and says so, rather than showing nothing. */}
            {typesId !== p.id && (
              <p className="text-xs text-fieldiq-muted">
                {p.lineItemTypes.length > 0
                  ? p.lineItemTypes.map((t) => LINE_ITEM_TYPE_LABELS[t]).join(' · ')
                  : 'No line item types chosen'}
              </p>
            )}

            {typesId === p.id && (
              <fieldset className="pt-2 border-t border-fieldiq-border">
                <legend className="sr-only">Line item types a {p.name} works</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                  {LINE_ITEM_TYPE_OPTIONS.map((o) => (
                    <label key={o.value} className="flex items-center gap-2 text-xs text-fieldiq-text cursor-pointer">
                      <input
                        type="checkbox"
                        checked={typesDraft.has(o.value)}
                        onChange={() => toggleDraftType(o.value)}
                        className="accent-fieldiq-accent"
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => handleSaveTypes(p.id)}
                    disabled={saving}
                    className="px-3 py-1.5 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg disabled:opacity-50"
                  >
                    Save line item types
                  </button>
                  <button onClick={() => setTypesId(null)} className="px-3 py-1.5 text-xs text-fieldiq-text">
                    Cancel
                  </button>
                </div>
              </fieldset>
            )}
          </div>
        ))}

        {isAdmin && (
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
            <p className="text-sm font-medium text-white mb-3">Add Position</p>
            <div className="flex items-center gap-2">
              <input
                value={addName}
                onChange={(e) => { setAddName(e.target.value); setAddError(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                className={`${inputCls} flex-1 ${addError ? 'border-red-500' : ''}`}
                placeholder="Position name"
                aria-label="New position name"
              />
              <button
                onClick={handleAdd}
                disabled={saving || !addName.trim()}
                className="px-4 py-2 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg disabled:opacity-50 flex items-center gap-1"
              >
                <Plus size={14} /> Add
              </button>
            </div>
            {addError && <p className="text-xs text-red-400 mt-1.5">{addError}</p>}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmDelete !== null}
        title="Delete position"
        body={`Delete “${confirmDelete?.name ?? ''}”? If any team member still holds it, it is deactivated instead of deleted.`}
        confirmLabel="Delete"
        loading={saving}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
